import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Grid from '@material-ui/core/Grid';
import { observer } from 'mobx-react';

import { Loading } from 'core';
import { useStore } from 'core/store';
import Section from 'core/components/section';
import { Card } from 'core/components/card';
import ValueField from 'core/components/value-field';
import Label from 'core/components/label/label';
import { useQueryParams } from 'core/hooks/useQueryParams';
import LabelWithCopyButton from 'core/components/label-with-copy-button';
import CollapsibleCard from 'core/components/collapsible-card';
import { brandConstants, isBranchEnv } from 'brand-config';

import { agentDetailsSourceFromPolicy } from './agent-details-source';
import useStyles from './agent-details.styles';

const getFullName = (user) => {
  if (user?.attributes?.given_name || user?.attributes?.family_name) {
    return `${user?.attributes?.given_name || ''} ${user?.attributes?.family_name || ''}`;
  }
  return undefined;
};

/**
 * Agent / agency / lead-source card.
 *
 * Without a `source` prop it describes the account's most recent active/future policy (the Customer
 * page sidebar, original behavior). With a `source` (see agent-details-source.js) it describes that
 * record instead — the policy being viewed on the Policy page, or the offer on the Offer page — and
 * does not depend on the account store being loaded (#35737). `collapsible` renders the card
 * collapsed behind its title (expanded on click), as the Offer and Policy pages want it.
 */
const AgentDetails = ({ source, collapsible = false, initialOpen = false, sectionType }) => {
  const classes = useStyles();
  const { account, search, affinityLookups } = useStore();
  const usesAccountPolicy = source === undefined;
  const recentPolicy = usesAccountPolicy ? account.policies.getMostRecentActiveOrFuturePolicy() : null;
  const details = usesAccountPolicy ? agentDetailsSourceFromPolicy(recentPolicy) : source;

  const customerAffinityCode = details?.affinity;
  const affinityData = affinityLookups.data.find(({ affinity }) => affinity === customerAffinityCode);
  const initialProducerCognitoUsername = details?.salesRep;
  const soldProducer = details?.bindRep;
  const initialLeadSource = details?.leadSource;
  const isAgentSold = customerAffinityCode && affinityData?.isAgency;
  const affinityInfo = isAgentSold && affinityData;
  const agencyInfo = affinityLookups.agencyInformation[affinityData?.data?.informationId || customerAffinityCode];
  const serviceAgreement = affinityData?.data?.serviceAgreement;

  const [agentCognitoUser, setAgentCognitoUser] = useState();
  const [loading, setLoading] = useState(true);
  const [searchingStaffUsers, setSearchingStaffUsers] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const queryParams = useQueryParams();
  const agentDetailsElement = useRef(null);

  useEffect(() => {
    // Only the account-backed card waits on the account; an explicit source (offer/policy page)
    // already has its data — the Offer page may have no account at all.
    const accountLoading = usesAccountPolicy && (account.accountLoading || !account.id);
    const isLoading = accountLoading || searchingStaffUsers || affinityLookups.partialLoading;
    setLoading(isLoading);
  }, [
    usesAccountPolicy,
    account.accountLoading,
    account.id,
    searchingStaffUsers,
    affinityLookups,
    affinityLookups.partialLoading,
    customerAffinityCode,
    agencyInfo
  ]);

  useEffect(() => {
    if (queryParams.scroll === 'agentDetails' && !scrolled && !loading && agentDetailsElement.current !== null) {
      agentDetailsElement.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
      setScrolled(true);
    }
  }, [loading, scrolled, queryParams]);

  // Codes already requested via getByAffinity, so a lookup that returns no row (stale/typo'd code →
  // 404 with retryOnNotFound: false) is fetched once, not on every partialLoading flip (devlo).
  const requestedAffinityRef = useRef(null);
  useEffect(() => {
    // With an explicit source nothing else guarantees the affinity row is loaded — the Customer and
    // Policy pages preload it with the account, but the Offer page may have no account, and typing in
    // the offer's Affinity field replaces affinityLookups.data with search results. Without the row
    // an agency offer would wrongly render as "Serviced By <brand>". getByAffinity is a no-op when
    // the row is present. (#35737 devlo)
    if (affinityData) {
      // Row present: forget the request so that, if the Affinity field's search later drops the row
      // from affinityLookups.data, one refetch is allowed. A row that never arrives never clears
      // the ref, so the 404 case still fetches once.
      requestedAffinityRef.current = null;
      return;
    }
    if (
      !usesAccountPolicy &&
      customerAffinityCode &&
      !affinityLookups.partialLoading &&
      requestedAffinityRef.current !== customerAffinityCode
    ) {
      requestedAffinityRef.current = customerAffinityCode;
      affinityLookups.getByAffinity(customerAffinityCode);
    }
  }, [usesAccountPolicy, customerAffinityCode, affinityData, affinityLookups, affinityLookups.partialLoading]);

  useEffect(() => {
    if (customerAffinityCode && !agencyInfo && !affinityLookups.partialLoading) {
      affinityLookups.getAgencyInformation(affinityData?.data?.informationId || customerAffinityCode);
    }
  }, [affinityLookups, isAgentSold, affinityData, customerAffinityCode, agencyInfo]);

  useEffect(() => {
    if (initialProducerCognitoUsername && isAgentSold) {
      // isEffectActive variable guards against stale writes when the user navigates between accounts
      // mid-fetch: the cleanup flips this to false so a slow/in-flight lookup
      // for the previous policy can't land after a newer effect has already
      // started, which would otherwise show the prior account's agent.
      // The MeiliSearch client doesn't expose abort here, so we ignore the
      // late response instead of cancelling it.
      let isEffectActive = true;
      const getAgentDataFromSearchCognitoIndex = async () => {
        setSearchingStaffUsers(true);
        const user = await search.searchStaffUsers({ username: initialProducerCognitoUsername });
        if (isEffectActive) {
          setAgentCognitoUser(user);
          setSearchingStaffUsers(false);
        }
      };

      getAgentDataFromSearchCognitoIndex();
      return () => {
        isEffectActive = false;
        // Reset loading flag here too — if cleanup fires mid-fetch, the guarded
        // setSearchingStaffUsers(false) above is skipped and the spinner sticks.
        setSearchingStaffUsers(false);
      };
      // Explicit else: the if branch returns the effect's cleanup, so falling
      // through would unconditionally clear agentCognitoUser on every effect
      // run — including agent-sold policies — causing a flash of empty state
      // before the fetch resolves.
      // biome-ignore lint/style/noUselessElse: see comment above
    } else {
      // Clear stale agent data when navigating to a policy without an agency-sold producer,
      // otherwise the previously loaded account's agent details persist in local state.
      setAgentCognitoUser(undefined);
    }
  }, [initialProducerCognitoUsername, isAgentSold, search]);

  if (!details) {
    return null;
  }

  // The agent rows describe the Primary Producer's staff user, so they need agentCognitoUser. On the
  // mutual brands the agency rows come from agency_information alone: migrated policies carry an
  // agency affinity but no Primary Producer, and staff still need the agency's contact details
  // (#38013). BIX keeps hiding the agency until there is a producer.
  const isMutual = !isBranchEnv;
  const showAgent = isAgentSold && agentCognitoUser;
  const showAgency = isAgentSold && (agentCognitoUser || isMutual);

  const body = (
    <Grid container className={classes.card}>
      {showAgency && (
        <Grid item xs={12}>
          <Label type="darkGreenSmallBold"> Agent </Label>
        </Grid>
      )}
      <Grid item xs={6}>
        <ValueField label="Primary Producer" value={initialProducerCognitoUsername || 'N/A'} />
      </Grid>
      <Grid item xs={6}>
        <ValueField label="Sold Producer" value={soldProducer || 'N/A'} />
      </Grid>
      {showAgent && (
        <>
          <Grid item xs={12}>
            <ValueField label="Agent Full Name" value={getFullName(agentCognitoUser) || 'N/A'} />
          </Grid>
          <Grid item xs={12} className={classes.separator}>
            <Label type="infoLabel" className={classes.copyButtonLabel}>
              Agent Email
            </Label>
            <LabelWithCopyButton type="infoValue">{agentCognitoUser?.attributes?.email || 'N/A'}</LabelWithCopyButton>
          </Grid>
        </>
      )}
      {showAgency && (
        <>
          <Label type="darkGreenSmallBold"> Agency </Label>
          <Grid item xs={12}>
            <ValueField label="Agency Name" value={agencyInfo?.name || affinityInfo?.name || 'N/A'} />
          </Grid>
          <Grid item xs={12}>
            <Label type="infoLabel" className={classes.copyButtonLabel}>
              Agency Email
            </Label>
            <LabelWithCopyButton type="infoValue">
              {agencyInfo?.email || affinityInfo?.ccEmail || 'N/A'}
            </LabelWithCopyButton>
          </Grid>
          <Grid item xs={6}>
            <ValueField label="Serviced By" value={serviceAgreement ? brandConstants.SHORT_NAME : 'Agency'} />
          </Grid>
          {!serviceAgreement && (
            <Grid item xs={12}>
              <Label type="infoLabel" className={classes.copyButtonLabel}>
                Agency Service Phone Number
              </Label>
              <LabelWithCopyButton type="infoValue">{agencyInfo?.servicing_phone_number || 'N/A'}</LabelWithCopyButton>
            </Grid>
          )}
          <Grid item xs={12} className={classes.separator}>
            <Label type="infoLabel" className={classes.copyButtonLabel}>
              Agency Phone Number
            </Label>
            <LabelWithCopyButton type="infoValue">
              {agencyInfo?.phone || affinityInfo?.phone || 'N/A'}
            </LabelWithCopyButton>
          </Grid>
        </>
      )}
      {showAgency && (
        <Grid item xs={12}>
          <Label type="darkGreenSmallBold"> Additional Information </Label>
        </Grid>
      )}
      <Grid item xs={6}>
        <ValueField label="Initial Lead Source" value={initialLeadSource || 'N/A'} />
      </Grid>
      <Grid item xs={6}>
        <ValueField label="Initial Affinity Code" value={customerAffinityCode || 'N/A'} />
      </Grid>
      {!isAgentSold && (
        <Grid item xs={6}>
          <ValueField label="Serviced By" value={brandConstants.SHORT_NAME} />
        </Grid>
      )}
    </Grid>
  );

  if (collapsible) {
    return (
      <div ref={agentDetailsElement}>
        <Section type={sectionType}>
          <CollapsibleCard
            dataCy="AgentDetails"
            initialOpen={initialOpen}
            content={<Label type="infoSubtitle">Agent Details</Label>}
            id="agentDetails"
          >
            {loading ? <Loading /> : body}
          </CollapsibleCard>
        </Section>
      </div>
    );
  }

  return (
    <div ref={agentDetailsElement}>
      <Section title="Agent Details" type={sectionType}>
        {loading ? <Loading /> : <Card id={'agentDetails'}>{body}</Card>}
      </Section>
    </div>
  );
};

AgentDetails.propTypes = {
  /** Record to describe (agent-details-source.js). Omit to describe the account's most recent policy. */
  source: PropTypes.shape({
    affinity: PropTypes.string,
    salesRep: PropTypes.string,
    bindRep: PropTypes.string,
    leadSource: PropTypes.string
  }),
  /** Render collapsed behind the "Agent Details" title; click to expand. */
  collapsible: PropTypes.bool,
  initialOpen: PropTypes.bool,
  /** Section header style, e.g. 'SubSection' to match the Offer settings sections. */
  sectionType: PropTypes.string
};

export default observer(AgentDetails);
